import * as React from "react";
import {
  IXGUIFieldBase,
  TOnChange,
  TXGUIField,
} from "../../../utils/interfaces/gui/fields.interfaces";

import { UNITS } from "../../../utils/constants/gui/units";
import { HexAlphaColorPicker, HexColorInput } from "react-colorful";
import { TBorder } from "../../../utils/types/gui/field.types";
import {
  Button,
  Divider,
  Fab,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useBreakpoint } from "../../../hooks/useBreakpoint";

const compareProps = (prevProps: any, nextProps: any) => {
  // return isEqual(prevProps, nextProps);
  return prevProps.currentValue === nextProps.currentValue;
};

interface XGUIBorderFieldProps extends IXGUIFieldBase {
  onChange: TOnChange<TBorder | undefined>;
  currentValue: TBorder;
}

// TODO PLAN TO ADD A SWITCH FOR MULTIPLE BORDER SIDE
const XGUIBorderField: React.FC<XGUIBorderFieldProps> = React.memo(
  ({ field, currentValue, onChange }) => {
    const value = currentValue ?? field.defaultValue ?? {};

    const bp = useBreakpoint();

    const setValue = (_value: TBorder) => {
      onChange(_value, field.name, bp);
    };

    const id = `XGUI-FIELD-${field.name}`;

    const section = (
      <React.Fragment>
        <Stack spacing={1} direction="row" mb={1}>
          <Typography
            component="label"
            htmlFor={id}
            textAlign="center"
            display="flex"
            width="100%"
            flex={1}
          >
            {field.label}
          </Typography>
        </Stack>
        <Stack spacing={1} direction="row">
          <Field field={field} id={id} value={value} setValue={setValue} />
          <BorderStyle id={id} value={value} setValue={setValue} />
          <ColorPicker id={id} value={value} setValue={setValue} />
        </Stack>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />
      </React.Fragment>
    );

    return section;
  },
  compareProps
);

export default XGUIBorderField;

interface FieldProps {
  id: string;
  value: TBorder;
  setValue: (newValue: TBorder) => void;
  field: TXGUIField;
}

const Field: React.FC<FieldProps> = ({ id, value, field, setValue }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { width } = value;

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [unit, setUnit] = React.useState<string>(
    value && value.width ? value.width.replace(/[0-9]/g, "") : "px"
  );

  React.useEffect(() => {
    if (width) {
      if (!width.includes(unit)) {
        const _unit = width.replace(/[0-9]/gi, "");

        if (_unit.length > 0) setUnit(_unit);
      }
    }
  }, [width]);

  return (
    <Stack
      className="XGUIFieldOption"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flex={1}
    >
      <TextField
        id={id + "-" + "Width"}
        fullWidth
        hiddenLabel
        size="small"
        type="number"
        placeholder="Taille"
        value={width && parseInt(width.replace(/[^0-9]/gi, "") ?? "0")}
        onChange={(e) => {
          const _val = e.target.value + unit;

          setValue({ ...value, width: _val });
        }}
        InputProps={{
          endAdornment: (
            <>
              <Button
                id={`XGUIFieldOption-Btn-${name}`}
                aria-controls={
                  open ? `XGUIFieldOption-Menu-${name}` : undefined
                }
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                color="inherit"
              >
                {unit}
              </Button>
              <Menu
                id={`XGUIFieldOption-Menu-${name}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": `XGUIFieldOption-Btn-${name}`,
                }}
                sx={{
                  ".MuiList-root": {
                    p: 0,

                    ".MuiButtonBase-root": {
                      fontSize: 10,
                      px: 1,
                      fontWeight: 600,
                      justifyContent: "center",
                    },
                  },
                }}
              >
                {(field.units ?? UNITS).map((_unit) => {
                  return (
                    <MenuItem
                      key={_unit}
                      onClick={() => {
                        setUnit((oldUnit) => {
                          const _value =
                            (width ?? "0").replace(oldUnit, "") + _unit;

                          if (width || _unit === "auto") {
                            setValue({ ...value, width: _value });
                          }

                          return _unit;
                        });

                        handleClose();
                      }}
                    >
                      {_unit}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ),
        }}
      />
    </Stack>
  );
};

interface ColorPickerProps {
  id: string;
  value: TBorder;
  setValue: (newValue: TBorder) => void;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ id, value, setValue }) => {
  const [open, setOpen] = React.useState<boolean>(false);

  const color = value.color ?? "#000000";

  return (
    <Stack
      className="XGUIColorPicker"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      position="relative"
      flex={1}
    >
      <Stack width="100%" direction="row" spacing={1}>
        <Stack
          sx={{
            backgroundImage: 'url("/images/transparent.png")',
            backgroundPosition: "center",
            backgroundSize: "cover",
            borderRadius: "50%",
            position: "relative",
          }}
        >
          <Fab
            // aria-describedby={`XGUIColorPicker-Popover-${field.name}`}
            onClick={() => setOpen((old) => !old)}
            // id={`XGUIColorPicker-Btn-${field.name}`}
            size="small"
            color="inherit"
            sx={{
              width: 30,
              height: 30,
              position: "relative",
            }}
            style={{ backgroundColor: color }}
          />
          {open && (
            <Stack
              sx={{
                bgcolor: "white",
                p: 1,
                borderRadius: 2,
                position: "absolute",
                bottom: 40,
                left: 0,
                zIndex: 1051,
                ".react-colorful": {
                  width: 160,
                  height: 160,
                  ".react-colorful__hue, .react-colorful__alpha": {
                    height: 15,
                  },

                  ".react-colorful__saturation-pointer": {
                    borderRadius: 0.5,
                    width: 20,
                    height: 20,
                  },

                  ".react-colorful__hue-pointer, .react-colorful__alpha-pointer":
                    {
                      height: 15,
                      width: 13,
                      borderRadius: 0.5,
                      cursor: "pointer",
                    },
                },
              }}
            >
              <HexAlphaColorPicker
                color={color}
                onChange={(newColor) => {
                  // onChange(newColor, field.name);
                  setValue({ ...value, color: newColor });
                }}
              />
            </Stack>
          )}
        </Stack>

        <TextField
          id={id}
          sx={{ flex: 1 }}
          hiddenLabel
          size="small"
          placeholder="Couleur"
          value={color}
          InputProps={{
            inputComponent: HexInput,
            inputProps: {
              placeholder: "#FFFFFF",
              color: color,
              onChange: (newColor: any) => {
                // onChange(newColor, field.name);
                setValue({ ...value, color: newColor });
              },
              alpha: true,
              prefixed: true,
            },
          }}
        />
      </Stack>
    </Stack>
  );
};

const HexInput = React.forwardRef((props, ref) => {
  React.useImperativeHandle(ref, () => ({
    focus: () => {},
  }));

  return <HexColorInput {...props} />;
});

interface BorderStyleProps {
  id: string;
  value: TBorder;
  setValue: (newValue: TBorder) => void;
}

const BorderStyle: React.FC<BorderStyleProps> = ({ id, value, setValue }) => {
  return (
    <Stack
      className="XGUISelectOption"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flex={1}
    >
      <Select
        id={id}
        fullWidth
        value={value.style ?? "solid"}
        onChange={(e) => {
          setValue({ ...value, style: e.target.value as any });
        }}
        native
        size="small"
        MenuProps={{
          sx: {
            ".MuiList-root": {
              p: 0,

              ".MuiButtonBase-root": {
                fontSize: 10,
                px: 1,
                fontWeight: 600,
                textAlign: "center",
              },
            },
          },
        }}
      >
        <option value="solid">Solide</option>
        <option value="dotted">Pointillet</option>
        <option value="dashed">Tiret</option>
      </Select>
    </Stack>
  );
};
